import {
  UpdateBundleItemsPayload,
  UpdateCataloguePayload,
} from 'types/masterData'
import Api from './axiosInstance'
import { API_MASTER_DATA } from 'utils/constants'

type FetchMasterDataParams = {
  limit: number
  page: number
  search?: string
  filters?: any
  barcodeToken?: string | null
  barcode?: string | null
}

const fetchMasterDataDetails = ({
  limit,
  page,
  search,
  filters,
  barcodeToken,
  barcode,
}: FetchMasterDataParams) => {
  let options: any = {}

  if (search) {
    options.search = search
  }

  if (filters) {
    options = { ...options, ...filters }
  }

  const params = new URLSearchParams({
    limit,
    page,
    ...options,
  })

  // Advance search by barcode: scope the list to the uploaded barcode set
  if (barcodeToken) {
    params.set('barcodeToken', barcodeToken)
  }

  // Advance search by barcode: typed/pasted exact barcodes (comma-separated)
  if (barcode) {
    params.set('barcode', String(barcode))
  }

  return Api.get({
    url: `${API_MASTER_DATA.MASTER_DATA}?${params}`,
  })
}

const fetchMasterDataById = (id: string) => {
  return Api.get({
    url: `${API_MASTER_DATA.MASTER_DATA}/${id}`,
  })
}

const updateMasterData = (
  id: string,
  data:
    | UpdateCataloguePayload
    | (UpdateCataloguePayload & UpdateBundleItemsPayload),
) => {
  return Api.patch({
    url: `${API_MASTER_DATA.MASTER_DATA}/${id}`,
    data: data,
  })
}

const dowloadMasterData = ({
  filters,
  barcodeToken,
  barcode,
}: {
  filters: Record<string, any>
  barcodeToken?: string | null
  barcode?: string | null
}) => {
  const params = new URLSearchParams(filters)

  // Advance search by barcode: scope the export to the uploaded barcode set
  if (barcodeToken) {
    params.set('barcodeToken', barcodeToken)
  }

  // Advance search by barcode: typed/pasted exact barcodes (comma-separated)
  if (barcode) {
    params.set('barcode', String(barcode))
  }

  return Api.get({
    url: `${API_MASTER_DATA.MASTER_DATA_EXPORT}?${params}`,
  })
}

// Advance search by barcode: upload a single-column CSV of barcodes and get a
// short-lived token (valid 60 min) that scopes the catalogue list/export.
const uploadBarcodeSearchFile = (file: File) => {
  const formData = new FormData()
  // field name MUST be "file"
  formData.append('file', file)

  return Api.post({
    url: API_MASTER_DATA.MASTER_DATA_BARCODE_SEARCH,
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// To Upload a Master data file in server and update master data
const importMasterData = async (data: any) => {
  return Api.post({
    url: API_MASTER_DATA.MASTER_DATA_IMPORT,
    data: data,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// To Upload a catalogue-categories (sku, categories) CSV — chunked upload,
// same contract as the master-data import; processed asynchronously.
const importCatalogueCategories = async (data: any) => {
  return Api.post({
    url: API_MASTER_DATA.MASTER_DATA_IMPORT_CATEGORIES,
    data: data,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// Fresh categories for the filter dropdown. Served under /catalogue rather
// than /fresh-category, so filtering master data does not require Fresh
// category-management rights. - { categories: [{ _id, name, isActive }] }
const fetchMasterDataFreshCategories = () =>
  Api.get({ url: API_MASTER_DATA.MASTER_DATA_FRESH_CATEGORIES })

// To Upload a Master data file in server and update master data
const syncErpPricing = async (id: string) => {
  return Api.post({
    url: `${API_MASTER_DATA.MASTER_DATA}/${id}/sync-erp-pricing`,
  })
}

export {
  fetchMasterDataDetails,
  updateMasterData,
  fetchMasterDataById,
  dowloadMasterData,
  importMasterData,
  importCatalogueCategories,
  syncErpPricing,
  uploadBarcodeSearchFile,
  fetchMasterDataFreshCategories,
}
